/**
 * GraphQL Share Mutation Hooks
 *
 * Hooks for sharing operations: create share links, consume tokens.
 *
 * Feature: 080-critique-sharing
 * Tasks: T021
 */

import { useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// ============================================================================
// GraphQL Mutations
// ============================================================================

/**
 * Mutation to create a shareable link for a critique session
 */
const CREATE_SHARE_LINK_MUTATION = gql`
  mutation CreateShareLink($input: CreateShareLinkInput!) {
    createShareLink(input: $input) {
      shareUrl
      shareTokenId
      expiresAt
      critiqueSessionId
    }
  }
`;

/**
 * Mutation to consume a share token (increment use count)
 */
const CONSUME_SHARE_TOKEN_MUTATION = gql`
  mutation ConsumeShareToken($token: String!) {
    consumeShareToken(token: $token)
  }
`;

/**
 * Mutation to share a critique with one or more recipients
 */
const SHARE_CRITIQUE_MUTATION = gql`
  mutation ShareCritique($input: ShareCritiqueInput!) {
    shareCritique(input: $input) {
      success
      message
      totalRecipients
      successCount
      failureCount
      results {
        recipientId
        recipientEmail
        success
        message
        shareEventId
        shareUrl
        emailStatus
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface ShareLink {
  shareUrl: string;
  shareTokenId: string;
  expiresAt: string;
  critiqueSessionId: string;
}

interface CreateShareLinkInput {
  critiqueSessionId: string;
}

interface CreateShareLinkData {
  createShareLink: ShareLink | null;
}

interface ConsumeShareTokenData {
  consumeShareToken: boolean;
}

export type ShareEmailStatus = 'pending' | 'sent' | 'failed' | 'skipped';

export interface ShareCritiqueRecipientResult {
  recipientId: string | null;
  recipientEmail: string | null;
  success: boolean;
  message: string;
  shareEventId: string | null;
  shareUrl: string | null;
  emailStatus: ShareEmailStatus;
}

export interface ShareCritiqueResult {
  success: boolean;
  message: string;
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  results: ShareCritiqueRecipientResult[];
}

export interface ShareRecipientInput {
  userId?: string;
  email?: string;
  message?: string;
}

interface ShareCritiqueInput {
  critiqueSessionId: string;
  recipients: ShareRecipientInput[];
}

interface ShareCritiqueData {
  shareCritique: ShareCritiqueResult | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for creating a share link
 *
 * Creates a new shareable link for a critique session.
 * The returned URL can be copied to clipboard and shared.
 */
export function useCreateShareLinkGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    CreateShareLinkData,
    { input: CreateShareLinkInput }
  >(CREATE_SHARE_LINK_MUTATION);

  const createShareLink = async (critiqueSessionId: string): Promise<ShareLink | null> => {
    logger.info('Sharing', 'Creating share link via GraphQL', { critiqueSessionId });

    try {
      const result = await mutate({
        variables: { input: { critiqueSessionId } },
      });

      const shareLink = result.data?.createShareLink;

      if (shareLink) {
        logger.info('Sharing', 'Share link created successfully', {
          critiqueSessionId,
          shareTokenId: shareLink.shareTokenId,
          expiresAt: shareLink.expiresAt,
        });
      }

      return shareLink || null;
    } catch (err) {
      logger.error('Sharing', 'Failed to create share link', { critiqueSessionId, error: err });
      throw err;
    }
  };

  return {
    createShareLink,
    data: data?.createShareLink || null,
    loading,
    error,
  };
}

/**
 * Hook for consuming a share token
 *
 * Called after successfully accessing shared content to increment the use count.
 * Should be called once per unique access.
 */
export function useConsumeShareTokenGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    ConsumeShareTokenData,
    { token: string }
  >(CONSUME_SHARE_TOKEN_MUTATION);

  const consumeShareToken = async (token: string): Promise<boolean> => {
    logger.info('Sharing', 'Consuming share token via GraphQL', { tokenPrefix: token.substring(0, 8) });

    try {
      const result = await mutate({
        variables: { token },
      });

      const consumed = result.data?.consumeShareToken ?? false;

      if (consumed) {
        logger.info('Sharing', 'Share token consumed successfully');
      } else {
        logger.warn('Sharing', 'Share token consumption returned false');
      }

      return consumed;
    } catch (err) {
      logger.error('Sharing', 'Failed to consume share token', { error: err });
      return false;
    }
  };

  return {
    consumeShareToken,
    data: data?.consumeShareToken ?? false,
    loading,
    error,
  };
}

/**
 * Hook for sharing a critique with recipients
 *
 * Shares a critique session with one or more recipients (by user ID or email).
 * Returns aggregate results for all recipients.
 */
export function useShareCritiqueGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    ShareCritiqueData,
    { input: ShareCritiqueInput }
  >(SHARE_CRITIQUE_MUTATION);

  const shareCritique = async (
    critiqueSessionId: string,
    recipients: ShareRecipientInput[]
  ): Promise<ShareCritiqueResult | null> => {
    logger.info('Sharing', 'Sharing critique via GraphQL', {
      critiqueSessionId,
      recipientCount: recipients.length,
    });

    try {
      const result = await mutate({
        variables: { input: { critiqueSessionId, recipients } },
      });

      const shareResult = result.data?.shareCritique;

      if (shareResult) {
        logger.info('Sharing', 'Critique shared', {
          critiqueSessionId,
          success: shareResult.success,
          successCount: shareResult.successCount,
          failureCount: shareResult.failureCount,
        });
      }

      return shareResult || null;
    } catch (err) {
      logger.error('Sharing', 'Failed to share critique', { critiqueSessionId, error: err });
      throw err;
    }
  };

  return {
    shareCritique,
    data: data?.shareCritique || null,
    loading,
    error,
  };
}

/**
 * Utility function to copy share link to clipboard
 *
 * Creates a share link and copies it to the clipboard.
 * Returns the full URL that was copied.
 */
export async function copyShareLinkToClipboard(
  createShareLink: (critiqueSessionId: string) => Promise<ShareLink | null>,
  critiqueSessionId: string
): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
  try {
    const shareLink = await createShareLink(critiqueSessionId);

    if (!shareLink) {
      return { success: false, error: 'Failed to create share link' };
    }

    // Copy the share URL to clipboard
    await navigator.clipboard.writeText(shareLink.shareUrl);

    logger.info('Sharing', 'Share link copied to clipboard', { shareUrl: shareLink.shareUrl });

    return { success: true, shareUrl: shareLink.shareUrl };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    logger.error('Sharing', 'Failed to copy share link', { error: errorMessage });
    return { success: false, error: errorMessage };
  }
}

/**
 * Combined hook for all share mutations
 */
export function useShareMutationsGraphQL() {
  return {
    useCreateShareLink: useCreateShareLinkGraphQL,
    useConsumeShareToken: useConsumeShareTokenGraphQL,
    useShareCritique: useShareCritiqueGraphQL,
  };
}
